import { useState } from 'react';
import { DateFormat } from '@gonfalon/format/constants';
import { DateTimePicker } from '@gonfalon/launchpad-experimental/DateTimePicker';
import { Button, DialogTrigger, Heading, Popover, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useStore } from '@tanstack/react-form';
import { clsx } from 'clsx';
import { addHours, startOfHour } from 'date-fns';
import { format as formatInTimeZone, toZonedTime } from 'date-fns-tz';

import { lastUsedTzid, saveTzidToCache } from '#utils/dateUtils';
import { trackFlagTargetingEvent } from '#utils/flags/flagAnalytics';

import type { PendingChangesFormType } from './usePendingChangesFormAdapter';

import styles from './PendingChangesModal.module.css';

export interface ScheduleChangesPopoverProps {
  form: PendingChangesFormType;
  setIsCustomScheduleSelected: (isCustomScheduleSelected: boolean) => void;
}

export function ScheduleChangesPopover({ form, setIsCustomScheduleSelected }: ScheduleChangesPopoverProps) {
  const { executionDate } = useStore(form.store, (state) => ({
    executionDate: state.values.executionDate,
  }));
  const [isOpen, setIsOpen] = useState(false);
  const defaultTzid = lastUsedTzid();
  const today = new Date();
  const defaultDate = executionDate ? new Date(executionDate) : startOfHour(addHours(today, 1));
  const [date, setDate] = useState(executionDate || defaultDate.getTime());
  // Pending timezone: only committed on "Update". Reset when popover opens so unsaved changes aren't leaked.
  const [pendingTzid, setPendingTzid] = useState(defaultTzid);

  const openPopover = () => {
    setPendingTzid(defaultTzid);
    setDate(executionDate || defaultDate.getTime());
    setIsOpen(true);
    trackFlagTargetingEvent('Schedule Changes Button Clicked');
  };

  return (
    <form.AppField name="isScheduleSelected">
      {(field) => {
        const scheduledLabel =
          executionDate &&
          formatInTimeZone(toZonedTime(executionDate, defaultTzid), DateFormat.MM_DD_YYYY_H_MM_A_Z, {
            timeZone: defaultTzid,
          });
        const ariaLabel = scheduledLabel
          ? `Schedule changes, currently scheduled in ${scheduledLabel}`
          : 'Schedule changes';

        const removeSchedule = () => {
          form.setFieldValue('executionDate', 0);
          field.handleChange(false);
          setIsOpen(false);
          setIsCustomScheduleSelected(false);
          setDate(0);
        };

        return (
          <DialogTrigger>
            <TooltipTrigger>
              <Button data-test-id="schedule-change" aria-label={ariaLabel} onPress={openPopover}>
                <Icon name="calendar-schedule" size="small" aria-hidden="true" />
                {scheduledLabel ? (
                  <>
                    {scheduledLabel}
                    <Icon
                      name="cancel-circle-outline"
                      size="small"
                      aria-label="Remove schedule"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        removeSchedule();
                        trackFlagTargetingEvent('Remove Schedule Button Clicked');
                      }}
                    />
                  </>
                ) : null}
              </Button>
              <Tooltip placement="top">Schedule changes</Tooltip>
            </TooltipTrigger>

            <Popover className={styles.disclosureMenu} isOpen={isOpen} onOpenChange={setIsOpen} placement="top start">
              <div className={styles.disclosureContent}>
                <Heading size="small">Schedule changes</Heading>
                {isOpen && (
                  <DateTimePicker
                    onChange={setDate}
                    className={clsx(styles.customSchedule, styles.flexColumn)}
                    timezoneProps={{
                      tzid: pendingTzid,
                      onChange: setPendingTzid,
                    }}
                    datepickerProps={{ minDate: today.getTime(), defaultDate: defaultDate?.getTime() }}
                  />
                )}
                <div className={clsx(styles.footerActions, styles.paddingTop300)}>
                  <Button
                    data-test-id="update-schedule-change"
                    variant="primary"
                    size="small"
                    onPress={() => {
                      setIsOpen(false);
                      setIsCustomScheduleSelected(true);
                      field.handleChange(true);
                      form.setFieldValue('executionDate', date);
                      saveTzidToCache(pendingTzid);
                      void form.validate('change');
                      trackFlagTargetingEvent('Update Schedule Button Clicked');
                    }}
                  >
                    Update
                  </Button>
                </div>
              </div>
            </Popover>
          </DialogTrigger>
        );
      }}
    </form.AppField>
  );
}
